import React, { useEffect, useState } from 'react';
import { Button, Form, Input, message, Modal, Rate } from 'antd';
import Title from 'antd/es/typography/Title';
import PropTypes from 'prop-types';
import { FieldBox, GreyText, FlexWrapper } from '../../../theme/common_style';
import { addProjectFeedbackApi, updateProjectFeedbackApi } from '../../../redux/project/apiRoute';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { RatingSection, RatingItem } from './ViewFeedback';
import { AvatarSelect } from '../../../components/common/AvatarSelect';
import { useResponsiveModalWidth } from '../../../hooks/useResponsiveModalWidth';

const { TextArea } = Input;

const CreateFeedback = ({ open, onCancel, edit, handleListing, member, memberLoading }) => {
  const [form] = Form.useForm();
  const location = useLocation();
  const { id } = location.state || {};
  const [loading, setLoading] = useState(false);
  const width = useResponsiveModalWidth(600);

  const handleSubmit = async (values) => {
    try {
      setLoading(true);

      const isEditMode = Boolean(edit);
      const apiPath = isEditMode ? updateProjectFeedbackApi : addProjectFeedbackApi;

      const res = await apiPath({
        ...(!isEditMode ? { project_id: Number(id) } : { id: Number(edit?.id) }),
        ...values,
        employee_id: Number(values?.employee_id)
      });
      if (res?.statusCode === 200) {
        toast.success(res?.message || `Successfully ${isEditMode ? 'updated' : 'added'}`);
        onCancel();
        handleListing();
      } else {
        toast.error(res?.message || 'Something went wrong');
      }
    } catch (error) {
      console.error('Submission error:', error);
      toast.error('Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (edit) {
      form.setFieldsValue({
        communication: edit?.communication,
        technical: edit?.technical,
        behaviour: edit?.behaviour,
        responsibility: edit?.responsibility,
        employee_id: edit?.employee?.id,
        note: edit?.note
      });
    }
  }, [edit]);

  return (
    <Modal
      open={open}
      maskClosable={false}
      onCancel={onCancel}
      centered
      prefixCls="antCustomModal"
      destroyOnClose
      width={width}
      footer={false}>
      <Title level={4} style={{ margin: 0, textAlign: 'center' }}>
        {edit ? 'Edit' : 'Create'} Feedback
      </Title>
      <Form form={form} style={{ margin: '10px 0 0px' }} onFinish={handleSubmit}>
        <FieldBox>
          <label htmlFor="employee_id">
            Team Member <span>*</span>
          </label>
          <Form.Item
            name="employee_id"
            rules={[{ required: true, message: 'Team Member is required' }]}>
            <AvatarSelect
              loading={memberLoading}
              options={member}
              placeholder="--Select Option--"
              isEmp={true}
            />
          </Form.Item>
        </FieldBox>

        <div style={{ marginTop: '20px' }}>
          <Title level={5} style={{ margin: '0 0 8px 0', fontWeight: '500' }}>
            Performance Ratings
          </Title>
          <RatingSection>
            <RatingItem>
              <span className="rating-label">Communication</span>
              <Form.Item name="communication" style={{ marginBottom: 0 }}>
                <Rate allowHalf />
              </Form.Item>
            </RatingItem>

            <RatingItem>
              <span className="rating-label">Technical Skills</span>
              <Form.Item name="technical" style={{ marginBottom: 0 }}>
                <Rate allowHalf />
              </Form.Item>
            </RatingItem>

            <RatingItem>
              <span className="rating-label">Behaviour</span>
              <Form.Item name="behaviour" style={{ marginBottom: 0 }}>
                <Rate allowHalf />
              </Form.Item>
            </RatingItem>

            <RatingItem>
              <span className="rating-label">Responsibility</span>
              <Form.Item name="responsibility" style={{ marginBottom: 0 }}>
                <Rate allowHalf />
              </Form.Item>
            </RatingItem>
          </RatingSection>
        </div>

        <FieldBox style={{ marginTop: '20px' }}>
          <FlexWrapper justify="space-between" width="100%">
            <label htmlFor="note">
              Note <span>*</span>
            </label>
            <GreyText>Max 300 words</GreyText>
          </FlexWrapper>
          <Form.Item
            name="note"
            rules={[
              { required: true, message: 'Description is required' },
              {
                validator: (_, value) => {
                  const trimmedValue = value ? value.trim() : '';
                  const wordCount = trimmedValue ? trimmedValue.split(/\s+/).length : 0;
                  if (!trimmedValue) {
                    return Promise.reject(new Error('Description cannot be empty'));
                  }
                  if (wordCount > 300) {
                    return Promise.reject(new Error('Maximum 300 words allowed'));
                  }
                  return Promise.resolve();
                }
              }
            ]}
            validateFirst>
            <TextArea
              style={{ resize: 'none' }}
              rows={10}
              placeholder="Write details here"
              onChange={(e) => {
                const value = e.target.value;
                const trimmedValue = value.trim();
                const wordCount = trimmedValue ? trimmedValue.split(/\s+/).length : 0;

                if (!trimmedValue) {
                  message.warning('Description cannot be empty or only spaces.');
                } else if (wordCount > 300) {
                  message.warning('You have exceeded the 300 word limit.');
                }
              }}
            />
          </Form.Item>
        </FieldBox>

        <FlexWrapper justify="end" gap="10px" margin="10px 0 0">
          <Button
            htmlType="submit"
            style={{ width: '140px' }}
            prefixCls="antCustomBtn"
            loading={loading}>
            {edit ? 'Update' : 'Save'}
          </Button>
        </FlexWrapper>
      </Form>
    </Modal>
  );
};

export default CreateFeedback;

CreateFeedback.propTypes = {
  open: PropTypes.bool,
  onCancel: PropTypes.func,
  edit: PropTypes.any,
  handleListing: PropTypes.func,
  member: PropTypes.any,
  memberLoading: PropTypes.bool
};
